import { useQueryClient } from "@tanstack/react-query";
import * as Sentry from "@sentry/react";
import toast from "react-hot-toast";
import { supabase } from "../../lib/supabase";
import { queryKeys } from "../../lib/queryKeys";
import {
  confirmDeletedSubtasks,
  deletedSubtaskTitles,
} from "../../lib/confirmDeletedSubtasks";
import { Task } from "../../types";

/**
 * Returns a `deleteSubtask(id)` that optimistically removes a single subtask
 * from its parent task in the query cache — reflected instantly in every
 * consumer of `useTasks` (e.g. the open task dialog) — then commits the DB
 * delete and rolls back on failure. Mirrors {@link useDeleteTask}, scoped to a
 * subtask instead of a task.
 *
 * This is the query-cache-aware replacement for the pre-migration delete; the
 * header delete button routes here so a subtask removal shows immediately
 * without waiting for a refetch.
 */
export function useDeleteSubtask() {
  const queryClient = useQueryClient();

  return async (subtaskId: string): Promise<boolean> => {
    const previousTasks = queryClient.getQueryData<Task[]>(queryKeys.tasks);
    const deletedTitles = deletedSubtaskTitles(previousTasks, [subtaskId]);

    // Optimistic removal — reflected instantly in every consumer of useTasks.
    // Left alone when the cache has not loaded: writing `[]` into it would flip
    // useTasks out of its pending state and drop the app's loading gate, and
    // the rollback's snapshot guard could not put it back.
    queryClient.setQueryData<Task[]>(queryKeys.tasks, (prev) =>
      prev
        ? prev.map((task) => ({
            ...task,
            subtasks: task.subtasks?.filter((st) => st.id !== subtaskId),
          }))
        : prev,
    );

    const { error } = await supabase
      .from("subtasks")
      .delete()
      .eq("id", subtaskId);

    if (error) {
      // Restore the whole snapshot if the delete fails.
      if (previousTasks) {
        queryClient.setQueryData(queryKeys.tasks, previousTasks);
      }
      Sentry.captureException(error, {
        extra: { mutation: "useDeleteSubtask", subtaskId },
      });
      toast.error("Failed to delete subtask. Please try again.");
      return false;
    }

    confirmDeletedSubtasks(deletedTitles);
    queryClient.invalidateQueries({ queryKey: queryKeys.tasks });
    return true;
  };
}

/**
 * Returns a `deleteSubtasks(ids)` — the batched sibling of
 * {@link useDeleteSubtask}, for callers that remove several subtasks as one
 * user action (saving a task dialog the user pruned subtasks from).
 *
 * Looping {@link useDeleteSubtask} there would issue one DELETE **and one
 * `queryKeys.tasks` invalidation per subtask**, and that key refetches the whole
 * task graph — tasks, subtasks, tags, attachments and links (see `useTasks`).
 * Four removed subtasks meant four full refetches landing in the middle of the
 * adds and updates the same save performs, so a late one could momentarily
 * re-render the board without the just-added subtasks.
 *
 * This removes every row in a single `.in('id', …)` statement, applies one
 * optimistic cache update, and invalidates once. Because it is one statement,
 * a failure deletes nothing and the snapshot restores every subtask — where the
 * loop could leave the earlier ids committed and the later ones not.
 *
 * Kept separate from the singular hook rather than replacing it: the explicit
 * delete-subtask confirmation is a single-subtask action and keeps its own
 * Sentry context and its own invalidation.
 */
export function useDeleteSubtasks() {
  const queryClient = useQueryClient();

  return async (subtaskIds: string[]): Promise<boolean> => {
    if (subtaskIds.length === 0) return true;

    const doomed = new Set(subtaskIds);
    const previousTasks = queryClient.getQueryData<Task[]>(queryKeys.tasks);
    const deletedTitles = deletedSubtaskTitles(previousTasks, subtaskIds);

    // Optimistic removal — reflected instantly in every consumer of useTasks.
    // Left alone when the cache has not loaded: writing `[]` into it would flip
    // useTasks out of its pending state and drop the app's loading gate, and
    // the rollback's snapshot guard could not put it back.
    queryClient.setQueryData<Task[]>(queryKeys.tasks, (prev) =>
      prev
        ? prev.map((task) => ({
            ...task,
            subtasks: task.subtasks?.filter((st) => !doomed.has(st.id)),
          }))
        : prev,
    );

    const { error } = await supabase
      .from("subtasks")
      .delete()
      .in("id", subtaskIds);

    if (error) {
      // Restore the whole snapshot if the delete fails.
      if (previousTasks) {
        queryClient.setQueryData(queryKeys.tasks, previousTasks);
      }
      Sentry.captureException(error, {
        extra: { mutation: "useDeleteSubtasks", subtaskIds },
      });
      toast.error("Failed to delete subtask. Please try again.");
      return false;
    }

    confirmDeletedSubtasks(deletedTitles);
    queryClient.invalidateQueries({ queryKey: queryKeys.tasks });
    return true;
  };
}
